/**
 * variant-sku-display.js  v7
 * ─────────────────────────────────────────────────────────────
 * 役割①  カラープルダウンをJSで生成・管理
 * 役割②  容量ボタン切替 → 適合製品品番・在庫バッジを更新
 *
 * 依存データ（product.liquidで埋め込み済み）:
 *   window.TYESO.colorVariants   ... [{color, handle}, ...]
 *   window.TYESO.currentHandle   ... 現在商品のhandle
 *   window.TYESO_VARIANTS        ... バリアント一覧
 *   window.TYESO_COMPAT_MAP      ... 容量別適合製品品番マップ
 *                                    例: {"470ml":["KT01-047"],"600ml":["KT01-060"]}
 *
 * [v7変更点]
 *   - productメタフィールドアプローチに変更
 *   - TYESO_COMPAT_MAP（容量別JSON）からバリアント切替時に品番を取得
 *   - variantメタフィールドへの依存を廃止
 * ─────────────────────────────────────────────────────────────
 */
(function () {
  'use strict';

  /* ============================================================
     ① カラープルダウン
     ============================================================ */
  function initColorSelector() {
    const tyeso = window.TYESO;
    if (!tyeso || !tyeso.colorVariants) return;

    let colors = tyeso.colorVariants;
    if (!Array.isArray(colors)) {
      colors = Object.values(colors);
    }
    if (colors.length === 0) return;

    const currentHandle = tyeso.currentHandle;

    const variantsEl = document.querySelector('[data-product-variants]');
    if (!variantsEl) return;

    const formOuter = variantsEl.closest('.product__form__outer')
                   || variantsEl.parentElement;
    if (!formOuter) return;

    const existing = document.querySelector('[data-tyeso-color-selector]');
    if (existing) existing.remove();

    const wrapper = document.createElement('div');
    wrapper.className = 'selector-wrapper';
    wrapper.setAttribute('data-tyeso-color-selector', '');

    const current = colors.find(c => c.handle === currentHandle);
    const currentLabel = current ? current.color : 'カラーを選択';

    wrapper.innerHTML = `
      <fieldset>
        <div class="select__fieldset">
          <legend class="radio__legend">
            <span class="radio__legend__label">
              <span class="radio__legend__option-name">カラー</span>
            </span>
          </legend>
          <div class="tyeso-color-select" data-tyeso-color-select>
            <button
              type="button"
              class="select-popout__toggle tyeso-color-toggle"
              aria-expanded="false"
              data-tyeso-toggle
            >
              <span data-tyeso-toggle-text>${currentLabel}</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false" style="margin-left:6px;flex-shrink:0"><path d="M10 4L6 8 2 4" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <ul class="select-popout__list tyeso-color-list" data-tyeso-list style="display:none;">
              ${colors.map(cv => `
                <li class="select-popout__item${cv.handle === currentHandle ? ' is-active' : ''}">
                  <a
                    href="/products/${cv.handle}"
                    class="select-popout__option${cv.handle === currentHandle ? ' is-selected' : ''}"
                    ${cv.handle === currentHandle ? 'aria-current="true"' : ''}
                  >
                    <span>${cv.color}</span>
                  </a>
                </li>
              `).join('')}
            </ul>
          </div>
        </div>
      </fieldset>
    `;

    formOuter.insertBefore(wrapper, variantsEl);

    const toggle = wrapper.querySelector('[data-tyeso-toggle]');
    const list   = wrapper.querySelector('[data-tyeso-list]');

    toggle.addEventListener('click', function (e) {
      e.stopPropagation();
      const isOpen = list.style.display !== 'none';
      list.style.display = isOpen ? 'none' : 'block';
      toggle.setAttribute('aria-expanded', String(!isOpen));
    });

    document.addEventListener('click', function () {
      list.style.display = 'none';
      toggle.setAttribute('aria-expanded', 'false');
    });

    list.addEventListener('click', function (e) {
      e.stopPropagation();
    });
  }

  /* ============================================================
     MutationObserver: DOMが書き換えられたら自動復元
     ============================================================ */
  function watchAndRestore() {
    const tyeso = window.TYESO;
    if (!tyeso || !tyeso.colorVariants || tyeso.colorVariants.length === 0) return;

    const observer = new MutationObserver(function () {
      if (!document.querySelector('[data-tyeso-color-selector]')) {
        initColorSelector();
      }
    });

    const target = document.querySelector('.product__form__outer')
                || document.querySelector('[data-product]')
                || document.body;

    observer.observe(target, { childList: true, subtree: true });
  }

  /* ============================================================
     ② 容量ボタン切替 → 適合製品品番・在庫バッジ更新
     ============================================================ */
  function initVariantDisplay() {
    const variants   = window.TYESO_VARIANTS;
    const compatMap  = window.TYESO_COMPAT_MAP || {};  // [v7] 容量別品番マップ

    if (!variants || variants.length === 0) return;

    const urlParams    = new URLSearchParams(window.location.search);
    const initId       = parseInt(urlParams.get('variant')) || variants[0].id;
    let currentVariant = variants.find(v => v.id === initId) || variants[0];

    function updatePage(variant) {
      if (!variant) return;
      currentVariant = variant;

      // 適合製品品番リスト
      // [v7] TYESO_COMPAT_MAPのキー(容量)でlookup
      const compatList = document.querySelector('[data-variant-sku-list]');
      if (compatList) {
        compatList.innerHTML = '';

        const vol  = variant.option1 || '';  // 例: "470ml", "600ml"
        const nums = compatMap[vol] || [];

        if (nums.length > 0) {
          nums.forEach(function (num) {
            const li = document.createElement('li');
            li.className = 'variant-sku__item';
            li.setAttribute('data-variant-sku-item', '');
            li.textContent = num;
            compatList.appendChild(li);
          });
        } else {
          const li = document.createElement('li');
          li.className = 'variant-sku__item variant-sku__item--empty';
          li.setAttribute('data-variant-sku-item', '');
          compatList.appendChild(li);
        }
      }

      // 在庫バッジ
      const badge = document.querySelector('[data-availability-badge]');
      if (badge) {
        badge.dataset.state = variant.available ? 'in' : 'out';
        const live = badge.querySelector('[data-availability-live]');
        if (live) {
          live.textContent = variant.available
            ? (badge.dataset.labelIn  || '在庫あり')
            : (badge.dataset.labelOut || '在庫なし');
        }
      }

      // URL更新
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url.toString());
    }

    // 容量ラジオボタン変更検知
    document.addEventListener('change', function (e) {
      const el = e.target;
      if (!el.matches('input[data-single-option-selector]')) return;
      const matched = variants.find(function (v) { return v.option1 === el.value; });
      if (matched) updatePage(matched);
    });

    // 初回描画
    updatePage(currentVariant);
  }

  /* ============================================================
     エントリポイント
     ============================================================ */
  function init() {
    initColorSelector();
    watchAndRestore();
    initVariantDisplay();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();